/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { PassImageRequest } from './types';
import { passImageConfig } from './config';

/**
 * Convert kebab-case to snake_case for filenames
 * @param kebabCase - string in kebab-case (e.g., 'senior-lifetime')
 * @returns string in snake_case (e.g., 'senior_lifetime')
 */
function kebabToSnakeCase(kebabCase: string): string {
    return kebabCase.replace(/-/g, '_');
}

/**
 * Calculate the image year for a given date and pass type.
 * Fourth-grade passes use school year (Sept-Aug) with folder named by END year.
 *
 * Decision: issueDateOverride was added because call sites were incorrectly calculating
 * the year from issue dates for fourth-grade passes. By centralizing this logic in the
 * resolver, we ensure correct images for historical passes and prevent future mistakes.
 *
 * @param date - The date to calculate the year from
 * @param passType - The type of pass
 * @returns The year string to use for the image folder
 */
function calculateImageYear(date: Date, passType: string): string {
    if (passType === 'fourth-grade') {
        // School year: Sept-Aug, folder uses END year
        const month = date.getUTCMonth() + 1; // 0-indexed to 1-indexed
        const year = date.getUTCFullYear();
        return (month >= 9 ? year + 1 : year).toString();
    }
    return date.getUTCFullYear().toString();
}

/**
 * Resolves the relative path for a pass image based on the tree structure
 * @param request - The image request parameters
 * @returns Array of possible paths to try (for fallback support)
 */
export function resolvePassImagePath(request: PassImageRequest): string[] {
    const {
        passType,
        variant,
        yearOverride,
        issueDateOverride,
        ldYearOverride,
        version,
    } = request;
    const config = passImageConfig[passType];

    if (!config) {
        throw new Error(`Unknown pass type: ${passType}`);
    }

    const paths: string[] = [];

    // Determine the filename based on variant type
    // Convert passType to snake_case for filenames, keep kebab-case for directories
    const passTypeForFilename = config.customFilename || kebabToSnakeCase(passType);
    let filename: string;
    if (variant.type === 'sized') {
        filename = `${passTypeForFilename}_${variant.size}.webp`;
    } else {
        filename = `${passTypeForFilename}_${variant.name}.webp`;
    }
    switch (config.structure) {
        case 'year-based': {
            // Priority: yearOverride > issueDateOverride > ldYearOverride > current date
            let yearToUse: string;

            if (yearOverride) {
                yearToUse = yearOverride;
            } else if (issueDateOverride) {
                yearToUse = calculateImageYear(new Date(issueDateOverride), passType);
            } else if (ldYearOverride) {
                yearToUse = ldYearOverride;
            } else {
                // Fallback to current date with pass-type-aware calculation
                yearToUse = calculateImageYear(new Date(), passType);
            }

            // Use original passType (kebab-case) for directory path, or customPath if specified
            const dirPath = config.customPath || passType;
            paths.push(`${dirPath}/${yearToUse}/${filename}`);
            break;
        }

        case 'version-based': {
            // Use original passType (kebab-case) for directory paths, or customPath if specified
            const dirPath = config.customPath || passType;
            // Try the specific version first (if provided)
            if (version) {
                paths.push(`${dirPath}/${version}/${filename}`);
            }

            // Try the fallback version
            if (config.fallbackVersion) {
                paths.push(`${dirPath}/${config.fallbackVersion}/${filename}`);
            }

            // Finally, try the root level for this pass type
            paths.push(`${dirPath}/${filename}`);
            break;
        }

        case 'flat': {
            paths.push(`${passType}/${filename}`);
            break;
        }

        case 'custom': {
            // For custom variants, the filename already includes the full name
            paths.push(`${passType}/${filename}`);
            break;
        }

        default: {
            throw new Error(`Unknown structure type for pass: ${passType}`);
        }
    }

    return paths;
}

/**
 * Validates that the requested variant is supported for the given pass type
 * @param request - The image request parameters
 * @returns true if valid, throws error if invalid
 */
export function validatePassImageRequest(request: PassImageRequest): boolean {
    const { passType, variant } = request;
    const config = passImageConfig[passType];

    if (!config) {
        throw new Error(`Unknown pass type: ${passType}`);
    }

    if (variant.type === 'sized') {
        if (!config.sizes?.includes(variant.size)) {
            throw new Error(
                `Size "${variant.size}" not available for pass type "${passType}". ` +
                    `Available sizes: ${config.sizes?.join(', ') || 'none'}`
            );
        }
    } else if (variant.type === 'custom') {
        if (!config.customVariants?.includes(variant.name)) {
            throw new Error(
                `Custom variant "${variant.name}" not available for pass type "${passType}". ` +
                    `Available variants: ${config.customVariants?.join(', ') || 'none'}`
            );
        }
    }

    return true;
}
